import { NotificationApplyOptions } from '@/common/static_data';
import { ShipmentFilterFormData } from '../nodeFormTypes';
import { BaseTriggerData } from './hooks/useTriggerFormInit';

/**
 * Builds the updated trigger object from the current shipment-filter form data.
 *
 * All three trigger-node customization panels (Alert, ETA, Location) share
 * identical logic for resolving the 11 common filter fields. Component-specific
 * fields (e.g. `change_by` for ETA) can be passed via `extraFields` and are
 * merged in before the computed filter values.
 *
 * @param data       - Current form values (snake_case keys from AntD Form).
 * @param prev       - The existing trigger slice from nodeData (used as fallback
 *                     for select-all flags that may not be written to the form).
 * @param applyType  - Active apply-type chip array (used to detect "select all").
 * @param extraFields - Optional node-specific fields to merge into the result.
 */
export function buildShipmentFilterUpdate<T extends BaseTriggerData>(
  data: ShipmentFilterFormData,
  prev: T | null | undefined,
  applyType: number[],
  extraFields?: Partial<T>,
  isExplicitSelectAll?: boolean
): T {
  // When a type was previously ALL-selected via the "select all types" shortcut,
  // the individual flag may never have been written to the form.
  // Fall back to the existing node value so removing one type doesn't wipe others.
  const resolveFlag = (
    formVal: boolean | undefined,
    prevVal: boolean | undefined
  ): boolean => formVal ?? prevVal ?? false;

  const saTeams = resolveFlag(data.select_all_teams, prev?.select_all_teams);
  const saOrigins = resolveFlag(
    data.select_all_origins,
    prev?.select_all_origins
  );
  const saProducts = resolveFlag(
    data.select_all_products,
    prev?.select_all_products
  );
  const saTransport = resolveFlag(
    data.select_all_transport_providers,
    prev?.select_all_transport_providers
  );
  const saDest = resolveFlag(
    data.select_all_destinations,
    prev?.select_all_destinations
  );

  const isSelectAll =
    isExplicitSelectAll !== undefined
      ? isExplicitSelectAll
      : applyType.length === NotificationApplyOptions.length &&
        saTeams &&
        saOrigins &&
        saProducts &&
        saTransport &&
        saDest;

  // Annotated separately so the compiler checks these field names. The `as T`
  // below can't be avoided, and it would otherwise accept any key at all.
  const filterFields: BaseTriggerData = {
    courier_ids: isSelectAll
      ? []
      : saTransport
        ? []
        : data.filter_transport_providers || [],
    team_ids: isSelectAll ? [] : saTeams ? [] : data.filter_teams || [],
    origin_ids: isSelectAll ? [] : saOrigins ? [] : data.filter_origins || [],
    destination_ids: isSelectAll
      ? []
      : saDest
        ? []
        : data.filter_destinations || [],
    product_ids: isSelectAll ? [] : saProducts ? [] : data.products || [],
    select_all: isSelectAll,
    select_all_teams: isSelectAll ? true : saTeams,
    select_all_origins: isSelectAll ? true : saOrigins,
    select_all_products: isSelectAll ? true : saProducts,
    select_all_transport_providers: isSelectAll ? true : saTransport,
    select_all_destinations: isSelectAll ? true : saDest,
  };

  return { ...prev, ...extraFields, ...filterFields } as T;
}
